import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import MicrosoftLogin from './components/MicrosoftLogin';
import MicrosoftLoginSuccess from './components/MicrosoftLoginSuccess';
import MicrosoftLoginError from './components/MicrosoftLoginError';

import Loja from './components/Loja';
import Carrinho from './components/Carrinho';
import Navbar from './components/Navbar';
import EmailVerification from './components/EmailVerification';
// import ForgotPassword from './components/ForgotPassword';
// import ResetPassword from './components/ResetPassword';
import PainelRepresentantes from './components/PainelRepresentantes';
import AdminProdutos from './components/AdminProdutos';
import AdminPedidos from './components/AdminPedidos';
import AdminDashboard from './components/AdminDashboard';
import Users from './components/Users';
import MeusPedidos from './components/MeusPedidos';
import Logs from './components/Logs';
import Chamados from './components/Chamados';
import ControleLoja from './components/ControleLoja';
import ControleAcessos from './components/ControleAcessos';
import { UserProvider, useUser } from './contexts/UserContext';
import { useTracking } from './hooks/useTracking';
import { buildApiUrl } from './utils/api';
import './App.css';

// Componente para proteger rotas que requerem permissões de administrador
const ProtectedAdminRoute = ({ children, requiredPermission = 'admin' }) => {
  const { user, isAdmin, canManageProducts, canManageUsers, canAccessLogs } = useUser();

  if (!user) {
    // Sempre redirecionar para Microsoft Login
    return <Navigate to="/microsoft-login" replace />;
  }

  // Verificar permissões baseadas no tipo de rota
  let hasPermission = false;
  
  switch (requiredPermission) {
    case 'admin':
      hasPermission = isAdmin();
      break;
    case 'products':
      hasPermission = canManageProducts();
      break;
    case 'users':
      hasPermission = canManageUsers();
      break;
    case 'logs':
      hasPermission = canAccessLogs();
      break;
    default:
      hasPermission = isAdmin();
  }

  if (!hasPermission) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '18px',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '2px solid #ff4444',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '500px'
        }}>
          <h1 style={{ color: '#ff4444', marginBottom: '20px' }}>🚫 Acesso Negado</h1>
          <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
            Você não tem permissões para acessar esta área administrativa.
          </p>
          <p style={{ marginBottom: '30px', fontSize: '14px', opacity: '0.8' }}>
            Esta área é restrita apenas para usuários com permissões adequadas.
          </p>
          <button 
            onClick={() => window.location.href = '/painel'}
            style={{
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Voltar ao Painel
          </button>
        </div>
      </div>
    );
  }

  return children;
};

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationUserId, setVerificationUserId] = useState(null);
  // Estados de recuperação de senha comentados
  // const [showForgotPassword, setShowForgotPassword] = useState(false);
  // const [showResetPassword, setShowResetPassword] = useState(false);
  // const [recoveryUserId, setRecoveryUserId] = useState(null);
  const [carrinhoNotification, setCarrinhoNotification] = useState(false);
  const [carrinhoQuantidade, setCarrinhoQuantidade] = useState(0);
  const [carrinhoCarregado, setCarrinhoCarregado] = useState(false);
  const location = useLocation();
  const { updateUser } = useUser();
  
  // Hook de tracking - registra pageviews automaticamente
  const { trackLogout } = useTracking();

  // Função para determinar o redirecionamento correto baseado no tipo de usuário
  const getLoginRedirect = () => {
    // Sempre redirecionar para Microsoft Login
    return '/microsoft-login';
  };

  useEffect(() => {
    // Verificar se há token salvo no localStorage
    const token = localStorage.getItem('token');
    const azureToken = localStorage.getItem('azureToken');
    const userData = localStorage.getItem('userData');
    
    // Se tem token Microsoft, restaurar autenticação sem verificar
    if (azureToken && userData) {
      try {
        const parsedUser = JSON.parse(userData);

        setIsAuthenticated(true);
        setUser(parsedUser);
        updateUser(parsedUser);
        
        // Carregar carrinho para usuários Microsoft também
        if (!carrinhoCarregado) {
          carregarQuantidadeCarrinho();
          setCarrinhoCarregado(true);
        }
        
        setLoading(false);
        return;
      } catch (error) {
        console.error('Erro ao carregar dados do usuário Microsoft:', error);
        localStorage.removeItem('userData');
      }
    }
    
    // Verificar token local apenas se não for Microsoft
    if (token && !azureToken) {
      fetch(buildApiUrl('/api/profile'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        // Token inválido ou expirado - limpar localStorage silenciosamente
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        throw new Error('Token inválido');
      })
      .then(data => {
        setIsAuthenticated(true);
        setUser(data.user);
        updateUser(data.user);
        // Carregar carrinho apenas uma vez
        if (!carrinhoCarregado) {
          carregarQuantidadeCarrinho();
          setCarrinhoCarregado(true);
        }
      })
      .catch((error) => {
        // Não logar erro - token inválido é esperado quando expira
        // Usuário será redirecionado para login automaticamente
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UseEffect adicional para garantir que o carrinho seja carregado quando o usuário estiver autenticado
  useEffect(() => {
    if (isAuthenticated && !loading && !carrinhoCarregado) {
      carregarQuantidadeCarrinho();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading]);

  const carregarQuantidadeCarrinho = async () => {
    // Evitar carregar se já foi carregado
    if (carrinhoCarregado) {
      return;
    }

    try {
      // Buscar token (local ou Azure)
      const token = localStorage.getItem('token');
      const azureToken = localStorage.getItem('azureToken');
      
      // Se não tiver nenhum token, não carregar
      if (!token && !azureToken) return;
      
      // Usar o token disponível (priorizar token local se existir)
      const authToken = token || azureToken;

      const response = await fetch(buildApiUrl('/api/carrinho'), {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const quantidade = data.quantidade_total || 0;
        setCarrinhoQuantidade(quantidade);
        setCarrinhoNotification(quantidade > 0);
      }
    } catch (error) {
      console.error('Erro ao carregar quantidade do carrinho:', error);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    updateUser(userData);
    
    // Carregar carrinho após login
    setCarrinhoCarregado(false); // Reset para permitir carregamento
    carregarQuantidadeCarrinho();
  };



  const logout = async () => {
    // Registrar logout no sistema de analytics
    await trackLogout();
    
    // Limpar todos os dados
    localStorage.removeItem('token');
    localStorage.removeItem('azureToken');
    localStorage.removeItem('azureTokenExpires');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUser(null);
    
    // Sempre redirecionar para Microsoft Login após logout
    window.location.href = '/microsoft-login';
  };

  const handleVerificationRequired = (userId) => {
    setVerificationUserId(userId);
    setShowVerification(true);
  };

  const handleVerificationSuccess = (token, userData) => {
    
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    updateUser(userData);
    setShowVerification(false);
    setVerificationUserId(null);
  };

  const handleBackToLogin = () => {
    setShowVerification(false);
    setVerificationUserId(null);
  };

  // Funções de recuperação de senha comentadas
  /*
  const handleRecoveryRequested = (userId) => {
    setRecoveryUserId(userId);
    setShowResetPassword(true);
    setShowForgotPassword(false);
  };

  const handlePasswordReset = () => {
    setShowResetPassword(false);
    setRecoveryUserId(null);
  };

  const handleBackToLoginFromRecovery = () => {
    setShowForgotPassword(false);
    setShowResetPassword(false);
    setRecoveryUserId(null);
  };
  */

  const handleCarrinhoOpen = () => {
    // Navegar para a página do carrinho
    window.location.href = '/carrinho';
  };

  const handleCarrinhoClose = () => {
    // Voltar para a página anterior
    window.history.back();
  };

  const handleProdutoAdicionado = (quantidade) => {
    setCarrinhoQuantidade(quantidade);
    setCarrinhoNotification(quantidade > 0);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Carregando...
      </div>
    );
  }

  return (
      <div className={`App ${location.pathname === '/painel' ? 'painel-page' : ''} ${location.pathname === '/home' ? 'loja-page' : ''} ${location.pathname === '/carrinho' ? 'carrinho-page' : ''} ${['/admin', '/admin-produtos', '/admin-pedidos', '/controle-loja', '/controle-acessos'].includes(location.pathname) ? 'admin-page' : ''} ${location.pathname === '/meus-pedidos' ? 'meus-pedidos-page' : ''} ${location.pathname === '/users' ? 'users-page' : ''} ${location.pathname === '/logs' ? 'logs-page' : ''} ${['/login', '/microsoft-login', '/microsoft-login-success', '/microsoft-login-error'].includes(location.pathname) ? 'auth-page' : ''}`}>
        {isAuthenticated && (location.pathname === '/home' || location.pathname === '/carrinho' || location.pathname === '/admin-produtos' || location.pathname === '/admin-pedidos' || location.pathname === '/admin' || location.pathname === '/users' || location.pathname === '/meus-pedidos' || location.pathname === '/logs' || location.pathname === '/controle-loja' || location.pathname === '/controle-acessos') && <Navbar onLogout={logout} onCarrinhoOpen={handleCarrinhoOpen} carrinhoNotification={carrinhoNotification} carrinhoQuantidade={carrinhoQuantidade} />}
        <div className="container">
          {showVerification ? (
            <EmailVerification 
              userId={verificationUserId}
              onVerificationSuccess={handleVerificationSuccess}
              onBackToLogin={handleBackToLogin}
            />
          ) : (
            // Componentes de recuperação de senha comentados
            /*
          ) : showForgotPassword ? (
            <ForgotPassword onRecoveryRequested={handleRecoveryRequested} />
          ) : showResetPassword ? (
            <ResetPassword 
              userId={recoveryUserId}
              onPasswordReset={handlePasswordReset}
              onBackToLogin={handleBackToLoginFromRecovery}
            />
          ) : (
            */
            <Routes>
              <Route 
                path="/login" 
                element={
                  isAuthenticated ? 
                  <Navigate to="/painel" replace /> : 
                  <Login onLogin={login} onVerificationRequired={handleVerificationRequired} />
                } 
              />
              <Route 
                path="/microsoft-login" 
                element={
                  isAuthenticated ? 
                  <Navigate to="/painel" replace /> : 
                  <MicrosoftLogin onLogin={login} onVerificationRequired={handleVerificationRequired} />
                } 
              />
              <Route 
                path="/microsoft-login-success" 
                element={<MicrosoftLoginSuccess onLogin={login} />} 
              />
              <Route 
                path="/microsoft-login-error" 
                element={<MicrosoftLoginError />} 
              />
              {/* Rota de registro comentada
}
              {/* Rota de esqueci minha senha comentada
              <Route 
                path="/forgot-password" 
                element={
                  isAuthenticated ? 
                  <Navigate to="/painel" replace /> : 
                  <ForgotPassword onRecoveryRequested={handleRecoveryRequested} />
                } 
              />
              */}
              <Route 
                path="/painel" 
                element={
                  isAuthenticated ? 
                  <PainelRepresentantes user={user} onLogout={logout} /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
              <Route 
                path="/admin-produtos" 
                element={
                  <ProtectedAdminRoute requiredPermission="products">
                    <AdminProdutos />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/admin-pedidos" 
                element={
                  <ProtectedAdminRoute requiredPermission="admin">
                    <AdminPedidos />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/meus-pedidos" 
                element={
                  isAuthenticated ? 
                  <MeusPedidos /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
              <Route 
                path="/chamados" 
                element={
                  isAuthenticated ? 
                  <Chamados /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedAdminRoute requiredPermission="admin">
                    <AdminDashboard />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/users" 
                element={
                  <ProtectedAdminRoute requiredPermission="users">
                    <Users />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/logs" 
                element={
                  <ProtectedAdminRoute requiredPermission="logs">
                    <Logs />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/controle-loja" 
                element={
                  <ProtectedAdminRoute requiredPermission="admin">
                    <ControleLoja />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/controle-acessos" 
                element={
                  <ProtectedAdminRoute requiredPermission="admin">
                    <ControleAcessos />
                  </ProtectedAdminRoute>
                } 
              />
              <Route 
                path="/home" 
                element={
                  isAuthenticated ? 
                  <Loja user={user} onProdutoAdicionado={handleProdutoAdicionado} /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
              <Route 
                path="/carrinho" 
                element={
                  isAuthenticated ? 
                  <Carrinho onClose={handleCarrinhoClose} onCarrinhoUpdate={carregarQuantidadeCarrinho} /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
              <Route 
                path="/" 
                element={
                  isAuthenticated ? 
                  <Navigate to="/painel" replace /> : 
                  <Navigate to={getLoginRedirect()} replace />
                } 
              />
            </Routes>
          )}
        </div>
      </div>
  );
}

function App() {
  return (
    <UserProvider>
      <Router>
        <AppContent />
      </Router>
    </UserProvider>
  );
}

export default App; 